import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, fileUrl, patientQrUrl, pdfUrl, groomingPdfUrl, boardingPdfUrl, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { brandName, money, ageFromDob } from "@/lib/brands";
import { CheckoutDialog } from "@/components/CheckoutDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, FilePlus2, Pencil, Trash2, Syringe, Bug, Bone, Plus, QrCode, FileText, Image as ImgIcon, Download, PawPrint, Scissors, Home, IndianRupee, Receipt, UserCog } from "lucide-react";
import { toast } from "sonner";

const FieldRow = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-2 border-b border-border/50 last:border-0">
    <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-sm font-medium text-right">{value || "—"}</div>
  </div>
);

function RecordForm({ kind, patientId, onDone }) {
  const fieldKey = kind === "vaccinations" ? "vaccine_name" : kind === "dewormings" ? "medicine_name" : "product_name";
  const [d, setD] = useState({ [fieldKey]: "", date_given: "", next_due: "", administered_by: "", notes: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/${kind}`, { patient_id: patientId, ...d });
      toast.success("Record added");
      onDone();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };
  const lbl = kind === "vaccinations" ? "Vaccine" : kind === "dewormings" ? "Medicine" : "Product";
  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="space-y-1.5"><Label>{lbl} Name</Label>
        <Input value={d[fieldKey]} onChange={(e) => setD({...d, [fieldKey]: e.target.value})} required /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Date Given</Label>
          <Input type="date" value={d.date_given} onChange={(e) => setD({...d, date_given: e.target.value})} required /></div>
        <div className="space-y-1.5"><Label>Next Due</Label>
          <Input type="date" value={d.next_due} onChange={(e) => setD({...d, next_due: e.target.value})} /></div>
      </div>
      <div className="space-y-1.5"><Label>Administered By</Label>
        <Input value={d.administered_by} onChange={(e) => setD({...d, administered_by: e.target.value})} /></div>
      <div className="space-y-1.5"><Label>Notes</Label>
        <Textarea value={d.notes} onChange={(e) => setD({...d, notes: e.target.value})} rows={2} /></div>
      <Button type="submit" data-testid={`save-${kind}-btn`} className="w-full">Save</Button>
    </form>
  );
}

function CareSection({ title, icon: Icon, kind, patientId, items, onChange }) {
  const [open, setOpen] = useState(false);
  const fieldKey = kind === "vaccinations" ? "vaccine_name" : kind === "dewormings" ? "medicine_name" : "product_name";
  return (
    <Card className="p-5 border-border/60">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-primary" /><h3 className="font-primary font-semibold">{title}</h3></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`add-${kind}-btn`}><Plus className="h-4 w-4 mr-1" />Add</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add {title} Record</DialogTitle></DialogHeader>
            <RecordForm kind={kind} patientId={patientId} onDone={() => { setOpen(false); onChange(); }} />
          </DialogContent>
        </Dialog>
      </div>
      {items.length === 0 && <div className="text-sm text-muted-foreground py-2">No records yet.</div>}
      <div className="space-y-2">
        {items.map((r) => {
          const due = r.next_due ? new Date(r.next_due) : null;
          const overdue = due && due < new Date();
          return (
            <div key={r.id} className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2">
              <div>
                <div className="text-sm font-medium">{r[fieldKey]}</div>
                <div className="text-xs text-muted-foreground">Given: {r.date_given} {r.next_due && <>· Next due: <span className={overdue ? "text-destructive font-medium" : ""}>{r.next_due}</span></>}</div>
              </div>
              <Button size="icon" variant="ghost" onClick={async () => { await api.delete(`/${kind}/${r.id}`); onChange(); }}><Trash2 className="h-4 w-4" /></Button>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [cons, setCons] = useState([]);
  const [vacc, setVacc] = useState([]);
  const [dew, setDew] = useState([]);
  const [tf, setTf] = useState([]);
  const [groom, setGroom] = useState([]);
  const [board, setBoard] = useState([]);
  const [qrOpen, setQrOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [amtDlg, setAmtDlg] = useState({ open: false, cid: null, value: "" });
  const [xfer, setXfer] = useState({ open: false, doctorId: "", note: "", busy: false });
  const [doctors, setDoctors] = useState([]);
  const canService = user?.role === "admin" || user?.role === "staff";

  const load = async () => {
    const [pr, cr, vr, dr, tr] = await Promise.all([
      api.get(`/patients/${id}`),
      api.get(`/consultations`, { params: { patient_id: id } }),
      api.get(`/vaccinations`, { params: { patient_id: id } }),
      api.get(`/dewormings`, { params: { patient_id: id } }),
      api.get(`/tickfleas`, { params: { patient_id: id } }),
    ]);
    setP(pr.data); setCons(cr.data); setVacc(vr.data); setDew(dr.data); setTf(tr.data);
    if (user?.role === "admin" || user?.role === "staff") {
      const [gr, br] = await Promise.all([
        api.get(`/grooming`, { params: { patient_id: id } }).catch(() => ({ data: [] })),
        api.get(`/boarding`, { params: { patient_id: id } }).catch(() => ({ data: [] })),
      ]);
      setGroom(gr.data); setBoard(br.data);
    }
  };
  useEffect(() => { load(); }, [id, user]);

  const submitAmount = async () => {
    const { cid, value } = amtDlg;
    try {
      await api.put(`/consultations/${cid}/amount`, { amount: parseFloat(value || 0) });
      toast.success("Amount updated");
      setAmtDlg({ open: false, cid: null, value: "" });
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const openTransfer = async () => {
    setXfer({ open: true, doctorId: "", note: "", busy: false });
    try {
      const { data } = await api.get(`/doctors`, { params: { brand: p.brand } });
      setDoctors(data.filter((d) => d.id !== p.assigned_doctor_id));
    } catch (e) { setDoctors([]); }
  };

  const doTransfer = async () => {
    if (!xfer.doctorId) { toast.error("Select a doctor"); return; }
    setXfer((s) => ({ ...s, busy: true }));
    try {
      await api.post(`/patients/${id}/transfer`, { doctor_id: xfer.doctorId, note: xfer.note });
      const doc = doctors.find((d) => d.id === xfer.doctorId);
      toast.success(`${p.pet_name} transferred to ${doc?.name || "doctor"}`);
      setXfer({ open: false, doctorId: "", note: "", busy: false });
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Transfer failed");
      setXfer((s) => ({ ...s, busy: false }));
    }
  };

  const doDelete = async () => {    setDeleting(true);
    try {
      await api.delete(`/patients/${id}`);
      toast.success(`${p.pet_name} moved to Trash — auto-deletes in 7 days`);
      navigate("/patients");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Delete failed");
      setDeleting(false);
    }
  };

  if (!p) return <div className="p-8">Loading…</div>;

  // gather images across consultations
  const photoIds = cons.flatMap((c) => (c.observation_images || []).map((x) => ({ ...x, cid: c.id, date: c.created_at })));

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto animate-fade-in">
      <button onClick={() => navigate(-1)} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div className="flex items-center gap-4">
          {p.photo_file_id ? (
            <img src={fileUrl(p.photo_file_id)} alt={p.pet_name} data-testid="patient-photo-avatar"
                 className="w-16 h-16 rounded-xl object-cover border border-border shrink-0" />
          ) : (
            <div className="w-16 h-16 rounded-xl bg-muted border border-border grid place-items-center shrink-0">
              <PawPrint className="h-7 w-7 text-muted-foreground" />
            </div>
          )}
          <div>
            <div className="text-xs font-mono uppercase text-muted-foreground">{p.case_number}</div>
            <h1 className="font-primary text-3xl md:text-4xl font-bold tracking-tight">{p.pet_name}</h1>
            <p className="text-sm text-muted-foreground">{p.species}{p.breed ? `, ${p.breed}` : ""} · Owner: <span className="text-foreground font-medium">{p.owner_name}</span> · {p.phone}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Dialog open={qrOpen} onOpenChange={setQrOpen}>
            <DialogTrigger asChild><Button variant="outline" data-testid="qr-btn"><QrCode className="h-4 w-4 mr-2" />QR</Button></DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle>Patient QR Code</DialogTitle></DialogHeader>
              <div className="grid place-items-center p-4">
                <img src={patientQrUrl(id)} alt="patient qr" className="w-64 h-64 bg-white p-3 rounded-lg" />
                <div className="text-xs text-muted-foreground mt-3 font-mono">{p.case_number}</div>
              </div>
            </DialogContent>
          </Dialog>
          <Link to={`/patients/${id}/edit`}><Button variant="outline" data-testid="edit-patient-btn"><Pencil className="h-4 w-4 mr-2" />Edit</Button></Link>
          {(user?.role === "doctor" || user?.role === "admin") && (
            <Link to={`/consultations/new?patient=${id}`}><Button data-testid="new-consultation-for-patient-btn"><FilePlus2 className="h-4 w-4 mr-2" />New Consultation</Button></Link>
          )}
          {canService && (
            <>
              <CheckoutDialog patientId={id} onSettled={load}
                trigger={<Button variant="outline" data-testid="checkout-btn" className="border-primary/40 text-primary hover:bg-primary/10"><Receipt className="h-4 w-4 mr-2" />Settle &amp; Close</Button>} />
              <Link to={`/grooming/new?patient=${id}`}><Button variant="outline" data-testid="new-grooming-for-patient-btn"><Scissors className="h-4 w-4 mr-2" />Grooming</Button></Link>
              <Link to={`/boarding/new?patient=${id}`}><Button variant="outline" data-testid="new-boarding-for-patient-btn"><Home className="h-4 w-4 mr-2" />Boarding</Button></Link>
            </>
          )}
          {user?.role === "admin" && (
            <Button variant="outline" data-testid="transfer-patient-btn" onClick={openTransfer}
                    className="border-primary/40 text-primary hover:bg-primary/10">
              <UserCog className="h-4 w-4 mr-2" />Transfer
            </Button>
          )}
          {user?.role === "admin" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid="delete-patient-btn"
                        className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive">
                  <Trash2 className="h-4 w-4 mr-2" />Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete {p.pet_name}'s profile?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This moves the entire pet profile (and its consultations) to Trash. It will be hidden everywhere
                    and permanently deleted automatically after 7 days. An admin can restore it before then.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cancel-delete-patient-btn">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="confirm-delete-patient-btn" onClick={doDelete} disabled={deleting}
                                     className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    {deleting ? "Deleting…" : "Delete profile"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-5 border-border/60 lg:col-span-1">
          <h3 className="font-primary font-semibold mb-3">Profile</h3>
          <FieldRow label="Species" value={p.species} />
          <FieldRow label="Breed" value={p.breed} />
          <FieldRow label="Age" value={p.age} />
          <FieldRow label="Sex" value={p.sex} />
          <FieldRow label="Weight" value={p.weight} />
          <FieldRow label="Colour" value={p.colour} />
          <FieldRow label="Microchip" value={p.microchip_number} />
          <FieldRow label="Assigned Doctor" value={p.assigned_doctor_name} />
          <div className="border-t border-border/60 my-3" />
          <FieldRow label="Owner" value={p.owner_name} />
          <FieldRow label="Phone" value={p.phone} />
          <FieldRow label="Email" value={p.email} />
          <FieldRow label="Address" value={p.address} />
        </Card>

        <div className="lg:col-span-2">
          <Tabs defaultValue="timeline">
            <TabsList className="mb-4">
              <TabsTrigger value="timeline" data-testid="tab-timeline">Consultations</TabsTrigger>
              <TabsTrigger value="care" data-testid="tab-care">Vaccinations & Care</TabsTrigger>
              {canService && <TabsTrigger value="grooming" data-testid="tab-grooming">Grooming</TabsTrigger>}
              {canService && <TabsTrigger value="boarding" data-testid="tab-boarding">Boarding</TabsTrigger>}
              <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
            </TabsList>

            <TabsContent value="timeline" className="space-y-4">
              {cons.length === 0 && (
                <Card className="p-10 text-center border-border/60">
                  <FileText className="h-10 w-10 mx-auto opacity-30 mb-2" />
                  <div className="text-muted-foreground">No consultations yet for this patient.</div>
                  <Link to={`/consultations/new?patient=${id}`}><Button className="mt-4">Create first consultation</Button></Link>
                </Card>
              )}
              {cons.map((c) => (
                <Card key={c.id} className="p-5 border-border/60">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-muted-foreground">{new Date(c.created_at).toLocaleString()}</div>
                      <div className="font-primary font-semibold mt-1">{c.primary_diagnosis || "Consultation"}</div>
                      {c.chief_complaint && <div className="text-sm text-muted-foreground mt-1">CC: {c.chief_complaint}</div>}
                      {c.treatments?.length > 0 && <div className="text-xs mt-2 text-muted-foreground">Treatments: {c.treatments.join(", ")}</div>}
                      <div className="text-sm mt-2 flex items-center gap-2">
                        <span className="text-muted-foreground">Amount:</span>
                        <span className={`font-semibold ${c.amount ? "" : "text-muted-foreground"}`}>{c.amount ? money(c.amount) : "Not set"}</span>
                        {canService && <button onClick={() => setAmtDlg({ open: true, cid: c.id, value: c.amount ?? "" })} data-testid={`set-amount-${c.id}`} className="text-xs text-primary underline">{c.amount ? "edit" : "set"}</button>}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <a href={pdfUrl(c.id)} target="_blank" rel="noreferrer">
                        <Button variant="outline" size="sm" data-testid={`pdf-${c.id}`}><Download className="h-4 w-4 mr-1" />PDF</Button>
                      </a>
                      <Link to={`/consultations/${c.id}/edit`}><Button variant="ghost" size="sm"><Pencil className="h-4 w-4" /></Button></Link>
                    </div>
                  </div>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="care" className="grid md:grid-cols-1 gap-4">
              <CareSection title="Vaccinations" icon={Syringe} kind="vaccinations" patientId={id} items={vacc} onChange={load} />
              <CareSection title="Deworming" icon={Bone} kind="dewormings" patientId={id} items={dew} onChange={load} />
              <CareSection title="Tick & Flea" icon={Bug} kind="tickfleas" patientId={id} items={tf} onChange={load} />
            </TabsContent>

            {canService && (
              <TabsContent value="grooming" className="space-y-3">
                {groom.length === 0 && <Card className="p-8 text-center border-border/60 text-muted-foreground"><Scissors className="h-8 w-8 mx-auto opacity-30 mb-2" />No grooming bookings. Use the Grooming button above.</Card>}
                {groom.map((g) => (
                  <Card key={g.id} className="p-4 border-border/60 flex items-center justify-between gap-3" data-testid={`patient-grooming-${g.id}`}>
                    <Link to={`/grooming/${g.id}/edit`} className="min-w-0">
                      <div className="text-xs uppercase tracking-wider text-muted-foreground">{new Date(g.created_at).toLocaleDateString()} · {g.branch}</div>
                      <div className="font-medium text-sm">Grooming · {money(g.amount_paid)}</div>
                    </Link>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${g.status === "closed" ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>{g.status}</span>
                      <a href={groomingPdfUrl(g.id)} target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><Download className="h-4 w-4" /></Button></a>
                    </div>
                  </Card>
                ))}
              </TabsContent>
            )}

            {canService && (
              <TabsContent value="boarding" className="space-y-3">
                {board.length === 0 && <Card className="p-8 text-center border-border/60 text-muted-foreground"><Home className="h-8 w-8 mx-auto opacity-30 mb-2" />No boarding bookings. Use the Boarding button above.</Card>}
                {board.map((b) => (
                  <Card key={b.id} className="p-4 border-border/60 flex items-center justify-between gap-3" data-testid={`patient-boarding-${b.id}`}>
                    <Link to={`/boarding/${b.id}/edit`} className="min-w-0">
                      <div className="text-xs uppercase tracking-wider text-muted-foreground">{new Date(b.created_at).toLocaleDateString()} · {b.branch}</div>
                      <div className="font-medium text-sm">{b.boarding_type === "overnight" ? "Overnight" : "Day"} Boarding · {money(b.amount_paid)}</div>
                    </Link>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${b.status === "closed" ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>{b.status}</span>
                      <a href={boardingPdfUrl(b.id)} target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><Download className="h-4 w-4" /></Button></a>
                    </div>
                  </Card>
                ))}
              </TabsContent>
            )}

            <TabsContent value="gallery">
              <Card className="p-5 border-border/60">
                {photoIds.length === 0 ? (
                  <div className="text-center text-muted-foreground py-10">
                    <ImgIcon className="h-10 w-10 mx-auto opacity-30 mb-2" />
                    No photos uploaded yet.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {photoIds.map((ph) => (
                      <a key={ph.file_id} href={fileUrl(ph.file_id)} target="_blank" rel="noreferrer" className="block group relative">
                        <img src={fileUrl(ph.file_id)} alt={ph.caption || "obs"} className="w-full aspect-square object-cover rounded-lg border border-border" />
                        {ph.caption && <div className="text-xs mt-1 truncate">{ph.caption}</div>}
                      </a>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Dialog open={xfer.open} onOpenChange={(o) => setXfer((s) => ({ ...s, open: o }))}>
        <DialogContent className="max-w-md" data-testid="transfer-dialog">
          <DialogHeader><DialogTitle>Transfer {p.pet_name} to another doctor</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Reassign this patient to a doctor within {brandName(p.brand)}. The patient's branch updates to the doctor's branch.</p>
            <div className="space-y-1.5">
              <Label>Doctor</Label>
              <Select value={xfer.doctorId} onValueChange={(v) => setXfer((s) => ({ ...s, doctorId: v }))}>
                <SelectTrigger data-testid="transfer-doctor-select"><SelectValue placeholder={doctors.length ? "Select a doctor" : "No other doctors in this brand"} /></SelectTrigger>
                <SelectContent>
                  {doctors.map((d) => (
                    <SelectItem key={d.id} value={d.id} data-testid={`transfer-doctor-${d.id}`}>{d.name}{d.branch ? ` · ${d.branch}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Note (optional)</Label>
              <Textarea rows={2} value={xfer.note} onChange={(e) => setXfer((s) => ({ ...s, note: e.target.value }))} data-testid="transfer-note" placeholder="Reason for transfer…" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setXfer({ open: false, doctorId: "", note: "", busy: false })}>Cancel</Button>
            <Button onClick={doTransfer} disabled={xfer.busy || !xfer.doctorId} data-testid="confirm-transfer-btn"><UserCog className="h-4 w-4 mr-2" />{xfer.busy ? "Transferring…" : "Transfer"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={amtDlg.open} onOpenChange={(o) => setAmtDlg((s) => ({ ...s, open: o }))}>
        <DialogContent className="max-w-sm overflow-hidden p-0">
          <div className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground px-6 pt-6 pb-8">
            <DialogHeader>
              <DialogTitle className="font-primary text-xl">Consultation amount</DialogTitle>
            </DialogHeader>
            <div className="mt-3 flex items-end gap-1">
              <span className="font-primary text-3xl opacity-80">₹</span>
              <input
                autoFocus type="number" min="0" step="0.01"
                value={amtDlg.value}
                onChange={(e) => setAmtDlg((s) => ({ ...s, value: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && submitAmount()}
                data-testid="consultation-amount-input"
                placeholder="0.00"
                className="bg-transparent border-0 outline-none font-primary text-5xl tracking-tight w-full placeholder:text-primary-foreground/40"
              />
            </div>
          </div>
          <div className="p-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setAmtDlg({ open: false, cid: null, value: "" })}>Cancel</Button>
            <Button onClick={submitAmount} data-testid="save-consultation-amount-btn">Save amount</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
